import { getAllProgramas, programasVigentes } from '@/data/programasVigentes';
import {
  type CheckoutFormData,
  checkoutFormAuthenticatedSchema,
  checkoutFormGuestSchema,
} from '@eventflow/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

interface CheckoutFormProps {
  onSubmit: (data: CheckoutFormData) => Promise<void>;
  isLoading?: boolean;
  isAuthenticated?: boolean;
  defaultEmail?: string;
}

export default function CheckoutForm({
  onSubmit,
  isLoading = false,
  isAuthenticated = false,
  defaultEmail = '',
}: CheckoutFormProps) {
  // Usar el schema apropiado según el estado de autenticación
  const validationSchema = isAuthenticated
    ? checkoutFormAuthenticatedSchema
    : checkoutFormGuestSchema;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CheckoutFormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      customerName: '',
      customerEmail: defaultEmail,
      customerPhone: '',
      customerIdNumber: '',
      customerType: undefined,
      graduateProgram: '',
      password: '',
    },
  });

  // Observar el tipo de cliente para mostrar/ocultar el selector de programa
  const customerType = watch('customerType');
  const isEgresadoUnach = customerType === 'egresado_unach';

  // Estado para el filtro de búsqueda de programas
  const [programaFilter, setProgramaFilter] = useState('');

  // Obtener lista plana de programas (para conteo)
  const allProgramas = useMemo(() => getAllProgramas(), []);

  // Función para normalizar texto: quitar acentos y convertir a minúsculas
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Elimina diacríticos (acentos)
  };

  // Filtrar programas por unidad según búsqueda
  const programasPorUnidad = useMemo(() => {
    const filtroTrimmed = programaFilter.trim();
    const hayFiltroLocal = filtroTrimmed.length > 0;
    const filterNormalized = normalizeText(filtroTrimmed);

    const resultado: { nombreUnidad: string; municipio: string; programas: string[] }[] = [];

    for (const unidad of programasVigentes) {
      if (hayFiltroLocal) {
        // Buscar en nombre de unidad o municipio (sin importar acentos ni mayúsculas)
        const unidadCoincide =
          normalizeText(unidad.nombreUnidad).includes(filterNormalized) ||
          normalizeText(unidad.municipio).includes(filterNormalized);
        const programasFiltrados = unidadCoincide
          ? unidad.programas // Si la unidad/municipio coincide, mostrar todos sus programas
          : unidad.programas.filter((p) => normalizeText(p).includes(filterNormalized));

        if (programasFiltrados.length === 0) continue;

        resultado.push({
          nombreUnidad: unidad.nombreUnidad,
          municipio: unidad.municipio,
          programas: [...programasFiltrados].sort((a, b) => a.localeCompare(b, 'es')),
        });
      } else {
        // Sin filtro, mostrar todos
        resultado.push({
          nombreUnidad: unidad.nombreUnidad,
          municipio: unidad.municipio,
          programas: [...unidad.programas].sort((a, b) => a.localeCompare(b, 'es')),
        });
      }
    }

    return resultado.sort((a, b) => a.nombreUnidad.localeCompare(b.nombreUnidad, 'es'));
  }, [programaFilter]);

  const hayFiltro = programaFilter.trim().length > 0;

  // Contar programas mostrados
  const programasMostrados = programasPorUnidad.reduce((acc, u) => acc + u.programas.length, 0);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Información del Comprador</h2>

        {/* Nombre Completo */}
        <div className="mb-4">
          <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-2">
            Nombre Completo <span className="text-red-500">*</span>
          </label>
          <input
            {...register('customerName')}
            type="text"
            id="customerName"
            placeholder="Ej: Juan Pérez García"
            className={`input-field ${errors.customerName ? 'border-red-500' : ''}`}
            disabled={isLoading}
          />
          {errors.customerName && (
            <p className="mt-1 text-sm text-red-600">{errors.customerName.message}</p>
          )}
        </div>

        {/* Email (solo si NO está autenticado) */}
        {!isAuthenticated && (
          <>
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">¿No tienes cuenta?</p>
                  <p>
                    Se creará automáticamente con tu email y contraseña para que puedas gestionar
                    tus compras.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label
                htmlFor="customerEmail"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                {...register('customerEmail')}
                type="email"
                id="customerEmail"
                placeholder="ejemplo@correo.com"
                className={`input-field ${errors.customerEmail ? 'border-red-500' : ''}`}
                disabled={isLoading}
                autoComplete="email"
              />
              {errors.customerEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.customerEmail.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Te enviaremos la confirmación de compra a este email
              </p>
            </div>

            {/* Contraseña (solo si NO está autenticado) */}
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña <span className="text-red-500">*</span>
              </label>
              <input
                {...register('password')}
                type="password"
                id="password"
                placeholder="Mínimo 6 caracteres"
                className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                disabled={isLoading}
                autoComplete="new-password"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Usa una contraseña segura para proteger tu cuenta
              </p>
            </div>
          </>
        )}

        {/* Mensaje si está autenticado */}
        {isAuthenticated && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="h-5 w-5 text-green-600 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="text-sm text-green-800">
                <p>
                  Sesión iniciada como: <strong>{defaultEmail}</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Teléfono */}
        <div className="mb-4">
          <label htmlFor="customerPhone" className="block text-sm font-medium text-gray-700 mb-2">
            Teléfono <span className="text-red-500">*</span>
          </label>
          <input
            {...register('customerPhone')}
            type="tel"
            id="customerPhone"
            placeholder="Ej: 5512345678"
            className={`input-field ${errors.customerPhone ? 'border-red-500' : ''}`}
            disabled={isLoading}
            autoComplete="tel"
            maxLength={10}
            inputMode="numeric"
            pattern="[0-9]{10}"
            onInput={(e) => {
              // Solo permitir números y limitar a 10 dígitos
              const target = e.target as HTMLInputElement;
              const value = target.value.replace(/\D/g, '').slice(0, 10);
              target.value = value;
              setValue('customerPhone', value, { shouldValidate: true });
            }}
          />
          {errors.customerPhone && (
            <p className="mt-1 text-sm text-red-600">{errors.customerPhone.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Ingresa 10 dígitos (solo números). Ej: 5512345678
          </p>
        </div>

        {/* Tipo de Cliente */}
        <div className="mb-4">
          <label htmlFor="customerType" className="block text-sm font-medium text-gray-700 mb-2">
            Tipo de Cliente <span className="text-red-500">*</span>
          </label>
          <select
            {...register('customerType')}
            id="customerType"
            className={`input-field ${errors.customerType ? 'border-red-500' : ''}`}
            disabled={isLoading}
          >
            <option value="">Selecciona una opción</option>
            <option value="egresado_unach">Egresado UNACH</option>
            <option value="publico_general">Público en General</option>
          </select>
          {errors.customerType && (
            <p className="mt-1 text-sm text-red-600">{errors.customerType.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Indica si eres Egresado UNACH o Público en General
          </p>
        </div>

        {/* Programa Educativo (solo para Egresados UNACH) */}
        {isEgresadoUnach && (
          <div className="mb-4">
            <label
              htmlFor="graduateProgram"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Programa Educativo <span className="text-red-500">*</span>
            </label>

            {/* Campo de búsqueda */}
            <input
              type="text"
              placeholder="Buscar programa o facultad..."
              value={programaFilter}
              onChange={(e) => setProgramaFilter(e.target.value)}
              className="input-field mb-2"
              disabled={isLoading}
            />

            <select
              {...register('graduateProgram', {
                required: isEgresadoUnach ? 'Debes seleccionar tu programa educativo' : false,
              })}
              id="graduateProgram"
              className={`input-field ${errors.graduateProgram ? 'border-red-500' : ''}`}
              disabled={isLoading}
            >
              <option value="">Selecciona tu programa educativo</option>
              {/* Siempre mostrar agrupado por facultad/unidad (filtrado o no) */}
              {programasPorUnidad.map((unidad) => (
                <optgroup key={unidad.nombreUnidad} label={unidad.nombreUnidad}>
                  {unidad.programas.map((programa, index) => (
                    <option key={`${unidad.nombreUnidad}-${programa}-${index}`} value={programa}>
                      {programa}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errors.graduateProgram && (
              <p className="mt-1 text-sm text-red-600">{errors.graduateProgram.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Selecciona el programa del cual egresaste de la UNACH
            </p>
            {hayFiltro && (
              <p className="mt-1 text-xs text-blue-600">
                Mostrando {programasMostrados} de {allProgramas.length} programas
              </p>
            )}
          </div>
        )}

        {/* INE/Identificación */}
        <div className="mb-4">
          <label
            htmlFor="customerIdNumber"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            INE / Identificación Oficial
          </label>
          <input
            {...register('customerIdNumber')}
            type="text"
            id="customerIdNumber"
            placeholder="Opcional - Número de identificación oficial"
            className={`input-field ${errors.customerIdNumber ? 'border-red-500' : ''}`}
            disabled={isLoading}
          />
          {errors.customerIdNumber && (
            <p className="mt-1 text-sm text-red-600">{errors.customerIdNumber.message}</p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Necesario para validar tu identidad al ingresar al evento
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary px-8 py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Procesando...
            </div>
          ) : (
            'Confirmar Compra'
          )}
        </button>
      </div>
    </form>
  );
}
